import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { shareTypeMapping } from 'shared/const';

import SharePlaylistAndManage from './components/SharePlaylistAndManage';
import { usePlaylist } from '../../../context';

const toInvitedItem = (invitation) => ({
  id: `invitation-${invitation.id}`,
  invitationId: invitation.id,
  isInvited: true,
  name: invitation.name,
  email: invitation.email,
});
import {
  sharePlaylistGroup,
  sharePlaylistOrganization,
  sharePlaylistUser,
  unsharePlaylist,
} from './graphql/mutations';
import {
  listGroups,
  listInvitations,
  listMembers,
  listPendingPlaylistShares,
  listShares,
} from './graphql/queries';
import { SHARE_FIELDS, PLAYLIST_FIELDS } from './graphql/fragments';

const SharePlaylistContainer = () => {
  const [playlistId, setPlaylistId] = useState();
  const [shareType, setShareType] = useState(shareTypeMapping.NONE);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [initialSharedWith, setInitialSharedWith] = useState([]);
  const [groupsThatHasAccess, setGroupsThatHasAccess] = useState([]);
  const [usersWhoHasAccess, setUsersWhoHasAccess] = useState([]);
  const [formStatus, setFormStatus] = useState();
  const [searchResult, setSearchResult] = useState([]);

  const [groups, setGroups] = useState([]);
  const [searchGroupsTerm, setSearchGroupsTerm] = useState('');
  const [searchGroupsResult, setSearchGroupsResult] = useState([]);

  const [searchUsersResult, setSearchUsersResult] = useState([]);

  const [invitations, setInvitations] = useState([]);
  const [pendingInvitedAccess, setPendingInvitedAccess] = useState([]);

  const { t } = useTranslation();
  const {
    dialogs: { closeShareDialogHandler: onCloseHandler, shareValues },
    organization,
    useLazyGraphQLQuery,
    useGraphQLMutation,
  } = usePlaylist();

  const { id: organizationId } = organization || {};
  const { playlist, playlistName } = shareValues || {};

  const [listGroupsQuery, listGroupsResponse] = useLazyGraphQLQuery(listGroups);
  const [listMembersQuery, listMembersResponse] =
    useLazyGraphQLQuery(listMembers);
  const [listSharesQuery, listSharesResponse] = useLazyGraphQLQuery(listShares);
  const [listInvitationsQuery, listInvitationsResponse] =
    useLazyGraphQLQuery(listInvitations);
  const [listPendingSharesQuery, listPendingSharesResponse] =
    useLazyGraphQLQuery(listPendingPlaylistShares);

  const useMutationWithHandlers = (
    mutation,
    resultingShareType,
    setFormStatus,
    t
  ) => {
    const [mutateFunction] = useGraphQLMutation(mutation, {
      update(cache, { data }) {
        const {
          sharePlaylistGroup,
          sharePlaylistUser,
          sharePlaylistOrganization,
          unsharePlaylist,
        } = data;

        const { created, deleted } =
          sharePlaylistGroup ||
          sharePlaylistUser ||
          sharePlaylistOrganization ||
          unsharePlaylist;

        const existing = cache.readQuery({
          query: listShares,
          variables: {
            playlistId,
            organizationId,
          },
        });

        const currentShares = existing?.listShares || [];

        const remainingShares = currentShares.filter(
          (share) => !deleted.find((d) => d.id === share.id)
        );

        const updatedShares = [
          ...remainingShares,
          ...created.filter((c) => !remainingShares.find((s) => s.id === c.id)),
        ];

        cache.writeQuery({
          query: listShares,
          variables: {
            playlistId,
            organizationId,
          },
          data: {
            listShares: updatedShares,
          },
        });

        setFormStatus({
          success: true,
          message: t('playlist.updatePlaylistAccessMessage'),
        });

        cache.modify({
          fields: {
            listPlaylists(existingPlaylists = [], { readField, canRead } = {}) {
              const selectedPlaylistRef = existingPlaylists.find(
                (playlistRef) => readField('id', playlistRef) === playlistId
              );

              if (selectedPlaylistRef) {
                const selectedPlaylist = cache.readFragment({
                  id: cache.identify(selectedPlaylistRef),
                  fragment: PLAYLIST_FIELDS,
                });

                if (selectedPlaylist) {
                  cache.writeFragment({
                    id: cache.identify(selectedPlaylistRef),
                    fragment: PLAYLIST_FIELDS,
                    data: {
                      ...selectedPlaylist,
                      shareType: resultingShareType,
                    },
                  });
                }
              }
              return existingPlaylists.filter(canRead);
            },
          },
        });

        if (deleted.length > 0) {
          deleted.forEach((share) => {
            const shareRef = cache.identify(share);

            if (cache.readFragment({ id: shareRef, fragment: SHARE_FIELDS })) {
              cache.evict({ id: shareRef });
            }
          });
          cache.gc();
        }
      },
      onCompleted: () => {
        setFormStatus({
          success: true,
          message: t('playlist.updatePlaylistAccessMessage'),
        });
      },
      onError: () => {
        setFormStatus({
          success: false,
          message: t('errors.errorOccured'),
        });
      },
    });

    return mutateFunction;
  };

  const sharePlaylistGroupMutation = useMutationWithHandlers(
    sharePlaylistGroup,
    shareTypeMapping.GROUP,
    setFormStatus,
    t
  );

  const unsharePlaylistMutation = useMutationWithHandlers(
    unsharePlaylist,
    shareTypeMapping.NONE,
    setFormStatus,
    t
  );

  const sharePlaylistUserMutation = useMutationWithHandlers(
    sharePlaylistUser,
    shareTypeMapping.USER,
    setFormStatus,
    t
  );

  const sharePlaylistOrganizationMutation = useMutationWithHandlers(
    sharePlaylistOrganization,
    shareTypeMapping.ORGANIZATION,
    setFormStatus,
    t
  );

  const fetchMembersAndGroups = () => {
    listMembersQuery({
      variables: {
        pagination: { limit: 1000 },
        filter: { organizationId: { eq: organizationId } },
      },
    });

    listGroupsQuery({
      variables: {
        pagination: { limit: 1000 },
        filter: { organizationId: { eq: organizationId } },
      },
    });

    listInvitationsQuery({
      variables: {
        pagination: { limit: 1000 },
        organizationId,
      },
    });
  };

  useEffect(() => {
    fetchMembersAndGroups();
  }, [organizationId]);

  useEffect(() => {
    const { loading, data } = listMembersResponse;

    if (!loading && data?.listMembers) {
      setSearchResult(data.listMembers);
    }
  }, [listMembersResponse]);

  useEffect(() => {
    const { data, loading } = listGroupsResponse;

    if (data && !loading) {
      setGroups(data.listGroups);
    }
  }, [listGroupsResponse]);

  useEffect(() => {
    const { data, loading } = listInvitationsResponse;

    if (data && !loading) {
      setInvitations(data.listInvitations || []);
    }
  }, [listInvitationsResponse]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();

        const matchingUsers = searchResult
          .filter(
            ({ user }) =>
              user.name.toLowerCase().includes(term) ||
              user.email.toLowerCase().includes(term)
          )
          .map(({ user }) => user);

        const matchingInvitations = invitations
          .filter(
            (invitation) =>
              (invitation.name || '').toLowerCase().includes(term) ||
              (invitation.email || '').toLowerCase().includes(term)
          )
          .map((invitation) => toInvitedItem(invitation));

        setSearchUsersResult([...matchingUsers, ...matchingInvitations]);
      } else {
        setSearchUsersResult([]);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm, searchResult, invitations]);

  useEffect(() => {
    if (searchGroupsTerm !== '') {
      setSearchGroupsResult(
        groups.filter(({ name }) =>
          name.toLowerCase().includes(searchGroupsTerm.toLowerCase())
        )
      );
    }
  }, [searchGroupsTerm, groups]);

  useEffect(() => {
    if (playlistId && organizationId) {
      listSharesQuery({
        variables: {
          organizationId,
          playlistId,
        },
      });

      listPendingSharesQuery({
        variables: {
          organizationId,
          playlistId,
        },
      });
    }
  }, [organizationId, playlistId, listSharesQuery, listPendingSharesQuery]);

  useEffect(() => {
    const { loading, data } = listPendingSharesResponse;

    if (!loading && data?.listPendingPlaylistShares) {
      setPendingInvitedAccess(
        data.listPendingPlaylistShares.map((invitation) =>
          toInvitedItem(invitation)
        )
      );
    }
  }, [listPendingSharesResponse]);

  useEffect(() => {
    const { loading, data } = listSharesResponse;

    if (!loading && data?.listShares) {
      const { listShares } = data;

      const groupsThatHaveAccess = listShares
        .filter((share) => share.type === 'GROUP' && share.groupId !== null)
        .map((share) => {
          const group = groups.find((group) => group.id === share.groupId);
          return group || { id: share.groupId };
        });

      const usersWhoHaveAccess = listShares
        .filter((share) => share.type === 'USER' && share.userId !== null)
        .map((share) => {
          const member = searchResult.find(
            (member) => member.user.id === share.userId
          );
          return member ? member.user : { id: share.userId };
        });

      setGroupsThatHasAccess(groupsThatHaveAccess);
      setUsersWhoHasAccess(usersWhoHaveAccess);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [listSharesResponse, searchResult, playlistId, groups]);

  const onSubmitHandler = async ({
    shareType,
    newSharedList,
    removeAll,
    notifyNew,
  }) => {
    const ids = newSharedList.map(({ id }) => id);
    const type = removeAll ? shareTypeMapping.NONE : shareType;

    switch (type) {
      case shareTypeMapping.GROUP:
        if (ids.length === 0) {
          unsharePlaylistMutation({
            variables: {
              organizationId,
              playlistId,
            },
          });
        } else {
          sharePlaylistGroupMutation({
            variables: {
              groups: ids,
              organizationId,
              playlistId,
              notifyNew,
            },
          });
        }
        break;
      case shareTypeMapping.NONE:
        unsharePlaylistMutation({
          variables: {
            organizationId,
            playlistId,
          },
        });
        break;
      case shareTypeMapping.USER: {
        const userIds = newSharedList
          .filter((item) => !item.isInvited)
          .map((item) => item.id);
        const invitationIds = newSharedList
          .filter((item) => item.isInvited)
          .map((item) => item.invitationId);

        if (userIds.length > 0 || invitationIds.length > 0) {
          sharePlaylistUserMutation({
            variables: {
              users: userIds,
              invitations: invitationIds,
              organizationId,
              playlistId,
              notifyNew,
            },
          });
        } else {
          unsharePlaylistMutation({
            variables: {
              organizationId,
              playlistId,
            },
          });
        }
        break;
      }
      case shareTypeMapping.ORGANIZATION:
        sharePlaylistOrganizationMutation({
          variables: {
            organizationId,
            playlistId,
            notifyNew,
          },
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (playlist) {
      setPlaylistId(playlist.id);
      setShareType(shareTypeMapping[playlist.shareType]);
    }
  }, [playlist]);

  useEffect(() => {
    if (groupsThatHasAccess.length) {
      setShareType(shareTypeMapping.GROUP);
      setInitialSharedWith(groupsThatHasAccess);
    } else if (playlist) {
      setShareType(shareTypeMapping[playlist.shareType]);
      setInitialSharedWith([...usersWhoHasAccess, ...pendingInvitedAccess]);
    }
  }, [usersWhoHasAccess, groupsThatHasAccess, pendingInvitedAccess, playlist]);

  return (
    <SharePlaylistAndManage
      onClose={onCloseHandler}
      loading={isLoading}
      playlistName={playlistName}
      initialShareType={shareType}
      searchGroups={setSearchGroupsTerm}
      searchGroupsResult={searchGroupsResult}
      searchUsers={setSearchTerm}
      searchUsersResult={searchUsersResult}
      onSubmitHandler={onSubmitHandler}
      initialSharedWith={initialSharedWith}
      formStatus={formStatus}
      totalOrganizationMembers={
        organization?.membersCount ? organization.membersCount : 0
      }
      hasGroups={groups.length > 0}
    />
  );
};

export default SharePlaylistContainer;
